import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, CheckCircle2, Loader2, Play, RefreshCw, FileText, ListTodo, Users, FolderSync } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// ==================== INTERFACES ====================

interface SyncBatchResult {
  batch: {
    offset: number;
    limit: number;
    processed: number;
    total: number;
  };
  leads_created: number;
  leads_updated: number;
  cases_created: number;
  has_more: boolean;
  next_offset: number | null;
  errors?: string[];
}

interface ImportBatchResult {
  batch: {
    offset: number;
    limit: number;
    processed: number;
    total: number;
  };
  tasks_imported: number;
  notes_imported: number;
  has_more: boolean;
  next_offset: number | null;
  errors?: string[];
}

interface SyncState {
  isRunning: boolean;
  currentBatch: number;
  totalBatches: number;
  totalProcessed: number;
  estimatedTotal: number;
  leadsCreated: number;
  leadsUpdated: number;
  casesCreated: number;
  errors: string[];
  completed: boolean;
}

interface ImportState {
  isRunning: boolean;
  currentBatch: number;
  totalBatches: number;
  totalProcessed: number;
  totalCases: number;
  totalTasksImported: number;
  totalNotesImported: number;
  errors: string[];
  completed: boolean;
}

// ==================== COMPONENT ====================

const AdminKommoImport = () => {
  const { t } = useTranslation("common");
  
  // Sync state (Step 1)
  const [syncState, setSyncState] = useState<SyncState>({
    isRunning: false,
    currentBatch: 0,
    totalBatches: 0,
    totalProcessed: 0,
    estimatedTotal: 539,
    leadsCreated: 0,
    leadsUpdated: 0,
    casesCreated: 0,
    errors: [],
    completed: false,
  });
  
  // Import state (Step 2)
  const [importState, setImportState] = useState<ImportState>({
    isRunning: false,
    currentBatch: 0,
    totalBatches: 0,
    totalProcessed: 0,
    totalCases: 0,
    totalTasksImported: 0,
    totalNotesImported: 0,
    errors: [],
    completed: false,
  });
  
  const [importType, setImportType] = useState<"both" | "tasks" | "notes">("both");
  const [batchSize] = useState(30);

  // ==================== STEP 1: SYNC LEADS ====================
  
  const startSyncLeads = useCallback(async () => {
    setSyncState({
      isRunning: true,
      currentBatch: 0,
      totalBatches: 0,
      totalProcessed: 0,
      estimatedTotal: 539,
      leadsCreated: 0,
      leadsUpdated: 0,
      casesCreated: 0,
      errors: [],
      completed: false,
    });

    let offset = 0;
    let hasMore = true;
    let totalCreated = 0;
    let totalUpdated = 0;
    let totalCases = 0;
    let totalProcessed = 0;
    let allErrors: string[] = [];
    let batchNumber = 0;
    let estimatedTotal = 539;

    try {
      while (hasMore) {
        batchNumber++;
        console.log(`[Sync Leads] Starting batch ${batchNumber}, offset: ${offset}`);

        const { data, error } = await supabase.functions.invoke("kommo-sync", {
          body: {
            action: "sync_all_leads",
            offset,
            limit: batchSize,
          },
        });

        if (error) {
          console.error("[Sync Leads] Error:", error);
          allErrors.push(`Batch ${batchNumber}: ${error.message}`);
          break;
        }

        const result = data as SyncBatchResult;
        
        if (!result.batch) {
          allErrors.push(`Batch ${batchNumber}: Invalid response from server`);
          break;
        }

        estimatedTotal = result.batch.total;
        totalProcessed += result.batch.processed;
        totalCreated += result.leads_created;
        totalUpdated += result.leads_updated;
        totalCases += result.cases_created;
        
        if (result.errors && result.errors.length > 0) {
          allErrors.push(...result.errors);
        }

        const estimatedBatches = Math.ceil(estimatedTotal / batchSize);
        
        setSyncState((prev) => ({
          ...prev,
          currentBatch: batchNumber,
          totalBatches: estimatedBatches,
          totalProcessed,
          estimatedTotal,
          leadsCreated: totalCreated,
          leadsUpdated: totalUpdated,
          casesCreated: totalCases,
          errors: allErrors,
        }));

        hasMore = result.has_more;
        offset = result.next_offset || 0;

        // Delay between batches
        if (hasMore) {
          await new Promise((r) => setTimeout(r, 1500));
        }
      }

      setSyncState((prev) => ({
        ...prev,
        isRunning: false,
        completed: true,
      }));

      toast.success(`Sincronização concluída: ${totalCreated} leads criados, ${totalUpdated} atualizados, ${totalCases} casos criados`);
    } catch (err) {
      console.error("[Sync Leads] Exception:", err);
      setSyncState((prev) => ({
        ...prev,
        isRunning: false,
        errors: [...prev.errors, `Exception: ${err instanceof Error ? err.message : String(err)}`],
      }));
      toast.error("Falha na sincronização");
    }
  }, [batchSize]);

  // ==================== STEP 2: IMPORT TASKS/NOTES ====================

  const startBulkImport = useCallback(async () => {
    setImportState({
      isRunning: true,
      currentBatch: 0,
      totalBatches: 0,
      totalProcessed: 0,
      totalCases: 0,
      totalTasksImported: 0,
      totalNotesImported: 0,
      errors: [],
      completed: false,
    });

    let offset = 0;
    let hasMore = true;
    let totalTasks = 0;
    let totalNotes = 0;
    let totalProcessed = 0;
    let allErrors: string[] = [];
    let batchNumber = 0;
    let totalCases = 0;

    try {
      while (hasMore) {
        batchNumber++;
        console.log(`[Bulk Import] Starting batch ${batchNumber}, offset: ${offset}`);

        const { data, error } = await supabase.functions.invoke("kommo-sync", {
          body: {
            action: "bulk_import_kommo",
            offset,
            limit: batchSize,
            import_type: importType,
          },
        });

        if (error) {
          console.error("[Bulk Import] Error:", error);
          allErrors.push(`Batch ${batchNumber}: ${error.message}`);
          break;
        }

        const result = data as ImportBatchResult;
        
        if (!result.batch) {
          allErrors.push(`Batch ${batchNumber}: Invalid response from server`);
          break;
        }

        totalCases = result.batch.total;
        totalProcessed += result.batch.processed;
        totalTasks += result.tasks_imported;
        totalNotes += result.notes_imported;
        
        if (result.errors && result.errors.length > 0) {
          allErrors.push(...result.errors);
        }

        const estimatedBatches = Math.ceil(totalCases / batchSize);
        
        setImportState((prev) => ({
          ...prev,
          currentBatch: batchNumber,
          totalBatches: estimatedBatches,
          totalProcessed,
          totalCases,
          totalTasksImported: totalTasks,
          totalNotesImported: totalNotes,
          errors: allErrors,
        }));

        hasMore = result.has_more;
        offset = result.next_offset || 0;

        // Delay between batches
        if (hasMore) {
          await new Promise((r) => setTimeout(r, 1000));
        }
      }

      setImportState((prev) => ({
        ...prev,
        isRunning: false,
        completed: true,
      }));

      toast.success(
        t("kommoImport.importCompleted", {
          tasks: totalTasks,
          notes: totalNotes,
        })
      );
    } catch (err) {
      console.error("[Bulk Import] Exception:", err);
      setImportState((prev) => ({
        ...prev,
        isRunning: false,
        errors: [...prev.errors, `Exception: ${err instanceof Error ? err.message : String(err)}`],
      }));
      toast.error(t("kommoImport.importFailed"));
    }
  }, [importType, batchSize, t]);

  // ==================== CALCULATED VALUES ====================

  const syncProgressPercentage =
    syncState.estimatedTotal > 0 ? (syncState.totalProcessed / syncState.estimatedTotal) * 100 : 0;
    
  const importProgressPercentage =
    importState.totalCases > 0 ? (importState.totalProcessed / importState.totalCases) * 100 : 0;

  const isAnyRunning = syncState.isRunning || importState.isRunning;

  // ==================== RENDER ====================

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <RefreshCw className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">{t("kommoImport.bulkImportTitle")}</h1>
          <p className="text-muted-foreground">{t("kommoImport.bulkImportDescription")}</p>
        </div>
      </div>

      {/* ==================== STEP 1: SYNC LEADS ==================== */}
      
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Badge variant="outline" className="text-lg px-3 py-1">1</Badge>
          <h2 className="text-xl font-semibold">Sincronizar Leads do Kommo</h2>
        </div>
        
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FolderSync className="h-5 w-5" />
              Sincronização de Leads
            </CardTitle>
            <CardDescription>
              Busca todos os leads do pipeline de Asilo no Kommo e cria contatos e casos automaticamente
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={startSyncLeads}
              disabled={isAnyRunning}
              className="w-full h-12 text-lg"
              size="lg"
            >
              {syncState.isRunning ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Sincronizando...
                </>
              ) : (
                <>
                  <Users className="h-5 w-5 mr-2" />
                  Sincronizar Leads do Kommo
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Sync Progress */}
        {(syncState.isRunning || syncState.completed) && (
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {syncState.isRunning ? (
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                ) : syncState.completed ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : null}
                Progresso da Sincronização
              </CardTitle>
              <CardDescription>
                Batch {syncState.currentBatch} de ~{syncState.totalBatches}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Progress value={syncProgressPercentage} className="h-3" />
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{syncState.totalProcessed}</div>
                  <div className="text-sm text-muted-foreground">Leads Processados</div>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{syncState.estimatedTotal}</div>
                  <div className="text-sm text-muted-foreground">Total Estimado</div>
                </div>
                <div className="text-center p-3 bg-green-500/10 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{syncState.leadsCreated}</div>
                  <div className="text-sm text-muted-foreground">Leads Criados</div>
                </div>
                <div className="text-center p-3 bg-blue-500/10 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{syncState.casesCreated}</div>
                  <div className="text-sm text-muted-foreground">Casos Criados</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sync Errors */}
        {syncState.errors.length > 0 && (
          <Card className="border-destructive mb-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                Erros de Sincronização ({syncState.errors.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-32">
                <div className="space-y-2">
                  {syncState.errors.slice(0, 20).map((error, index) => (
                    <div key={index} className="flex items-start gap-2 text-sm">
                      <Badge variant="destructive" className="shrink-0">
                        {index + 1}
                      </Badge>
                      <span className="text-muted-foreground">{error}</span>
                    </div>
                  ))}
                  {syncState.errors.length > 20 && (
                    <div className="text-sm text-muted-foreground">
                      ... e mais {syncState.errors.length - 20} erros
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}
      </div>

      <Separator className="my-8" />

      {/* ==================== STEP 2: IMPORT TASKS/NOTES ==================== */}
      
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Badge variant="outline" className="text-lg px-3 py-1">2</Badge>
          <h2 className="text-xl font-semibold">Importar Tasks e Notes</h2>
        </div>

        {/* Import Type Selection */}
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-lg">{t("kommoImport.selectImportType")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Button
                variant={importType === "both" ? "default" : "outline"}
                onClick={() => setImportType("both")}
                disabled={isAnyRunning}
                className="flex gap-2"
              >
                <ListTodo className="h-4 w-4" />
                <FileText className="h-4 w-4" />
                {t("kommoImport.importBoth")}
              </Button>
              <Button
                variant={importType === "tasks" ? "default" : "outline"}
                onClick={() => setImportType("tasks")}
                disabled={isAnyRunning}
                className="flex gap-2"
              >
                <ListTodo className="h-4 w-4" />
                {t("kommoImport.importTasksOnly")}
              </Button>
              <Button
                variant={importType === "notes" ? "default" : "outline"}
                onClick={() => setImportType("notes")}
                disabled={isAnyRunning}
                className="flex gap-2"
              >
                <FileText className="h-4 w-4" />
                {t("kommoImport.importNotesOnly")}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Start Import Button */}
        <Card className="mb-4">
          <CardContent className="pt-6">
            <Button
              onClick={startBulkImport}
              disabled={isAnyRunning}
              className="w-full h-12 text-lg"
              size="lg"
            >
              {importState.isRunning ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  {t("kommoImport.importing")}
                </>
              ) : (
                <>
                  <Play className="h-5 w-5 mr-2" />
                  {t("kommoImport.startBulkImport")}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Import Progress */}
        {(importState.isRunning || importState.completed) && (
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {importState.isRunning ? (
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                ) : importState.completed ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : null}
                {t("kommoImport.progress")}
              </CardTitle>
              <CardDescription>
                {t("kommoImport.batchProgress", {
                  current: importState.currentBatch,
                  total: importState.totalBatches,
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Progress value={importProgressPercentage} className="h-3" />
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{importState.totalProcessed}</div>
                  <div className="text-sm text-muted-foreground">{t("kommoImport.casesProcessed")}</div>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{importState.totalCases}</div>
                  <div className="text-sm text-muted-foreground">{t("kommoImport.totalCases")}</div>
                </div>
                <div className="text-center p-3 bg-primary/10 rounded-lg">
                  <div className="text-2xl font-bold text-primary">{importState.totalTasksImported}</div>
                  <div className="text-sm text-muted-foreground">{t("kommoImport.tasksImported")}</div>
                </div>
                <div className="text-center p-3 bg-primary/10 rounded-lg">
                  <div className="text-2xl font-bold text-primary">{importState.totalNotesImported}</div>
                  <div className="text-sm text-muted-foreground">{t("kommoImport.notesImported")}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Import Errors */}
        {importState.errors.length > 0 && (
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                {t("kommoImport.errors")} ({importState.errors.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-48">
                <div className="space-y-2">
                  {importState.errors.map((error, index) => (
                    <div key={index} className="flex items-start gap-2 text-sm">
                      <Badge variant="destructive" className="shrink-0">
                        {index + 1}
                      </Badge>
                      <span className="text-muted-foreground">{error}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminKommoImport;
